import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';
import { route } from 'preact-router';

import localUtil from '../../lib/util';

@withText(props => ({
    coursesText: <Text id='course.upcoming-courses'>Upcoming Courses</Text>,
    viewAllText: <Text id='course.view-all-courses'>View all courses</Text>,
    noCoursesText: <Text id='course.no-upcoming-courses'>No upcoming courses</Text>,
    signedUpText: <Text id='course.signed-up'>signed up</Text>,
    unlimitedSlotsText: <Text id='course.unlimited-slots'>Open</Text>,
}))
@observer
class PublicCourses extends Component {
    constructor(props) {
        super(props);
        this.state = {
            courses: [],
            isLoading: true,
            error: null,
        };
    }

    loadCourses = async () => {
        const { customerId, limit = 6 } = this.props;
        if (!customerId) {
            this.setState({ isLoading: false, error: 'No customer ID provided' });
            return;
        }

        try {
            // Show all active public courses - filter client-side for registration not ended
            const response = await util.fetchApi('/api/courses/', { method: 'GET' }, {
                customer: customerId,
                hostname: typeof window !== 'undefined' ? window.location.hostname : '',
                public: 1,
                active: 1,
                limit: limit * 2, // Fetch more to allow for filtering
                sort: 'registrationEndDate',
            });

            let courses = response?.status === 200 ? (response.data || []) : [];

            // Filter: only show courses where registration hasn't ended yet
            const now = new Date();
            courses = courses.filter(course => {
                if (!course.registrationEndDate) return true; // No end date = always show
                return new Date(course.registrationEndDate) >= now;
            });

            // Limit to requested amount
            courses = courses.slice(0, limit);

            this.setState({
                courses,
                isLoading: false,
            });
        } catch (err) {
            this.setState({ isLoading: false, error: 'Failed to load courses' });
        }
    }

    componentDidMount() {
        this.loadCourses();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.customerId !== this.props.customerId) {
            this.loadCourses();
        }
    }

    formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'short',
        });
    }

    handleCourseClick = (course) => {
        route(`/course/${course.id}`);
    }

    viewAllCourses = () => {
        route('/course');
    }

    getSlotsInfo = (course) => {
        const { signedUpText, unlimitedSlotsText } = this.props;
        const totalParticipants = course.participants?.length || 0;
        const slots = course.slots || 0;

        if (slots === 0) {
            if (totalParticipants > 0) {
                return `${totalParticipants} ${signedUpText}`;
            }
            return unlimitedSlotsText;
        }
        return `${totalParticipants}/${slots} ${signedUpText}`;
    }

    getSlotsBadgeClass = (course) => {
        const totalParticipants = course.participants?.length || 0;
        const slots = course.slots || 0;

        if (slots === 0) {
            return 'text-bg-secondary';
        }
        if (totalParticipants >= slots) {
            return 'text-bg-danger';
        }
        if (totalParticipants >= slots * 0.8) {
            return 'text-bg-warning';
        }
        return 'text-bg-secondary';
    }

    render() {
        const { customerId, showTitle = true, showViewAll = true, classNames = '' } = this.props;
        const { coursesText, viewAllText, noCoursesText } = this.props;
        const { courses, isLoading, error } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading) {
            return (
                <div class={`public-courses ${classNames}`}>
                    <div class='text-center py-4'>
                        <i class='fa-duotone fa-spinner fa-spin fa-2x' />
                    </div>
                </div>
            );
        }

        if (error || courses.length === 0) {
            return null;
        }

        return (
            <div class={`public-courses mb-4 overflow-hidden ${classNames}`}>
                {showTitle && (
                    <div class='font-weight-lighter box-header d-flex flex-row justify-content-between align-items-center mb-3 px-3'>
                        <span>
                            <i class='fa-duotone fa-graduation-cap me-2' />
                            {coursesText}
                        </span>
                        {showViewAll && (
                            <span
                                class='text-primary small'
                                style='cursor: pointer;'
                                onClick={this.viewAllCourses}
                            >
                                {viewAllText} <i class='fa-solid fa-arrow-right ms-1' />
                            </span>
                        )}
                    </div>
                )}

                <div class='row g-2 mx-2'>
                    {courses.map((course, idx) => {
                        const hasImage = course.images?.length > 0;
                        const imageUrl = hasImage ? course.images[0].s3MediumLink : null;

                        return (
                            <div
                                key={course.id}
                                class='col-6'
                            >
                                <div
                                    class={`card h-100 overflow-hidden ${darkmode ? 'bg-dark border-secondary' : ''}`}
                                    style='cursor: pointer;'
                                    onClick={() => this.handleCourseClick(course)}
                                >
                                    {hasImage ? (
                                        <div
                                            class='card-img-top'
                                            style={`
                                                height: 120px;
                                                background-image: url(${imageUrl});
                                                background-size: cover;
                                                background-position: center;
                                            `}
                                        />
                                    ) : (
                                        <div
                                            class={`card-img-top d-flex justify-content-center align-items-center ${darkmode ? 'bg-secondary' : 'bg-light'}`}
                                            style='height: 120px;'
                                        >
                                            <i class='fa-duotone fa-graduation-cap fa-3x text-body-secondary' />
                                        </div>
                                    )}
                                    <div class='card-body pb-2'>
                                        <h6 class='card-title mb-2' style='line-height: 1.2;'>
                                            {course.title}
                                        </h6>
                                        {course.ingress && (
                                            <p class='card-text text-body-secondary small mb-2' style='
                                                display: -webkit-box;
                                                -webkit-line-clamp: 2;
                                                -webkit-box-orient: vertical;
                                                overflow: hidden;
                                            '>
                                                {course.ingress}
                                            </p>
                                        )}
                                    </div>
                                    <div class='card-footer bg-transparent border-0 pt-0'>
                                        <div class='d-flex flex-wrap gap-2 align-items-center'>
                                            {(course.dates?.length > 0 || course.startDate) && (
                                                <span class='badge text-bg-primary'>
                                                    <i class='fa-solid fa-calendar-days me-1' />
                                                    {this.formatDate(course.dates?.[0]?.startTime || course.startDate)}
                                                    {course.dates?.length > 1 && (
                                                        <span class='ms-1'>+{course.dates.length - 1}</span>
                                                    )}
                                                </span>
                                            )}
                                            {course.place && (
                                                <span class='badge text-bg-light text-body'>
                                                    <i class='fa-solid fa-location-dot me-1' />
                                                    {course.place}
                                                </span>
                                            )}
                                            {course.price > 0 && (
                                                <span class='badge text-bg-success'>
                                                    {util.format(course.price, 0)} kr (inkl. mva)
                                                </span>
                                            )}
                                            <span class={`badge ${this.getSlotsBadgeClass(course)}`}>
                                                <i class='fa-solid fa-users me-1' />
                                                {this.getSlotsInfo(course)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default PublicCourses;
